import { splitActionKey } from '@shared/utils/splitActionKey';
import type { CargoWithFreightItems } from '@api/features/laytime/getCargosAndLaytimes';
import { FreightRateType } from './constants';
import { round } from './math';

type InvoicedHiiRow = NonNullable<CargoWithFreightItems['HIIFreights']>[number] |
	NonNullable<CargoWithFreightItems['HIIOverages']>[number] |
	NonNullable<CargoWithFreightItems['HIIDeadfreights']>[number];

const isInvoicedHiiRow = (item: InvoicedHiiRow) => item.HireInvoiceItem.hireInvoiceId != null;

const getFDDNumbers = (cargo: CargoWithFreightItems) => {
	const cpQuantity = cargo.quantity;
	const cpQuantityMin = ((100 - (cargo.quantityTolerance ?? 0)) / 100) * cpQuantity;
	const cpQuantityMax = ((100 + (cargo.quantityTolerance ?? 0)) / 100) * cpQuantity;

	const totalActuallyLoaded = cargo.CargoPorts.reduce((total, cp) => {
		const { action } = splitActionKey(cp.portAndActionKey);

		if (action !== 'loading') {
			return total;
		}

		return total + (cp.blQuantity ?? cp.expectedQuantity ?? 0);
	}, 0);

	const totalActuallyDischarged = cargo.CargoPorts.reduce((total, cp) => {
		const { action } = splitActionKey(cp.portAndActionKey);

		if (action !== 'discharging') {
			return total;
		}

		return total + (cp.blQuantity ?? cp.expectedQuantity ?? 0);
	}, 0);

	let totalFreightAmount = 0;
	let totalOverageAmount = 0;
	let totalDeadfreightAmount = 0;

	if (totalActuallyLoaded > cpQuantity) {
		// Actually loaded more than CP
		if (totalActuallyLoaded >= cpQuantityMax) {
			// Actually loaded more than CP + tolerance %
			totalFreightAmount = cpQuantityMax;
			totalOverageAmount = round(totalActuallyLoaded - cpQuantityMax, 3);
		} else {
			totalFreightAmount = totalActuallyLoaded;
		}
	}

	if (totalActuallyLoaded === cpQuantity) {
		// loaded exactly CP quantity
		totalFreightAmount = cpQuantity;
	}

	if (totalActuallyLoaded !== 0 && totalActuallyLoaded < cpQuantity) {
		// Actually loaded less than CP
		if (totalActuallyLoaded <= cpQuantityMin) {
			// Actually loaded less than CP - tolerance %
			totalFreightAmount = totalActuallyLoaded;
			totalDeadfreightAmount = round(cpQuantityMin - totalActuallyLoaded, 3);
		} else {
			totalFreightAmount = totalActuallyLoaded;
		}
	}

	const calculateTotal = (
		items: InvoicedHiiRow[] | undefined,
		key: 'freightRate' | 'overageAmount' | 'deadfreightAmount',
	) => (items ?? [])
		.filter(isInvoicedHiiRow)
		.reduce((total: number, item) => {
			const rate = item.freightRate ?? cargo[key];

			if (rate == null) {
				return total;
			}

			const sign = item.HireInvoiceItem.isReversal ? -1 : 1;
			const freightType = item.freightType ?? cargo.freightType;

			if (key === 'freightRate' && freightType === FreightRateType.LUMPSUM) {
				return total + sign * (item.percentage / 100) * rate;
			}

			return total + sign * (item.percentage / 100) * item.quantity * rate;
		}, 0);

	const totalFreightInvoiced = calculateTotal(cargo.HIIFreights, 'freightRate');
	const totalOverageInvoiced = calculateTotal(cargo.HIIOverages, 'overageAmount');
	const totalDeadfreightInvoiced = calculateTotal(cargo.HIIDeadfreights, 'deadfreightAmount');

	return {
		totalFreightAmount,
		totalDeadfreightAmount,
		totalOverageAmount,
		totalActuallyLoaded,
		totalActuallyDischarged,
		totalFreightInvoiced,
		totalOverageInvoiced,
		totalDeadfreightInvoiced,
	};
};

export default getFDDNumbers;
